import { types, flow, getSnapshot, onSnapshot, applySnapshot } from 'mobx-state-tree'

import Menu from './types/menu'
import Screen from './types/screen'
import defaults from '../options/defaults'
import ComponentAttributes from './types/componentAttributes';
import Component from './types/component';
import MediaItem from './types/mediaItem';
import UserLevel from './types/userLevel';
import PipelineStatus from './types/pipelineStatus';
import User from './types/user';

import Renderer from '../renderer/renderer';

import USER from './user';

import API from '../service/api'

const AppModel = types
	.model('App', {
		id: types.optional(types.string, ''),
		creation_status: types.optional(types.string, '0'),
		show_creation_status_prompt: types.optional(types.boolean, true),
		device_scale: types.optional(types.number, 1.0),
		show_status_bar: types.maybeNull(types.boolean),
		settings: types.optional(types.string, ''),
		push_notification: types.optional(types.string, ''),
		in_preview_mode: types.maybeNull(types.boolean),
		previous_screen: types.maybeNull(types.string),
		current_screen: types.maybeNull(Screen),
		login: types.optional(types.string, JSON.stringify({
            options: defaults.login.options,
		})),
		menu: types.maybeNull(Menu),
		screens: types.optional(types.array(Screen), []),
		login_wall: types.maybeNull(Screen, null),
		premium_wall: types.maybeNull(Screen, null),
		header: types.maybeNull(Screen, null),
		footer: types.maybeNull(Screen, null),
		asset_store: types.optional(types.array(MediaItem), []),
		asset_store_item_in_detail_mode: types.maybeNull(MediaItem),
		media: types.optional(types.array(MediaItem), []),
		media_in_detail_mode: types.maybeNull(MediaItem),
		user_levels: types.optional(types.array(UserLevel), []),
		pipeline: types.maybeNull(PipelineStatus),
		in_menu_edit_mode: types.maybeNull(types.boolean),
		users: types.optional(types.array(User), []),
	})
	.actions(self => ({
		sendPush: flow(function* (message) {
			console.log('sendPush', message)

			return new Promise((resolve) => {
				// Check token is valid
				USER.getValidToken().then((access_token) => {
					console.log('valid_token', access_token)
					
					fetch(`${API.base}/notifications/send`, {
						method: 'POST',
						headers: {
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							token: access_token,
							message: message
						})
					})
					.then(data => {
						
						// TODO: check the fields are correct.

						console.log('sendPush response', data)

						resolve(true)
					})
					.catch((error) => {
						console.log('sendPush error', error)
						resolve(false)
					})
				})
			})
		}),
		setShowCreationStatusPrompt: flow(function* (bool) {
			self.show_creation_status_prompt = bool
		}),
		setDeviceScale: flow(function* (scale) {
			self.device_scale = scale

			self.setDeviceIsLoading(true)

			setTimeout(() => {
				window.dispatchEvent(new Event('resize'));
				self.setDeviceIsLoading(false)
			}, 1000)
		}),
		setDeviceIsLoading: flow(function* (bool) {
			self.device_is_loading = bool
		}),
		shouldShowStatusBar: flow(function* (bool) {
			self.show_status_bar = bool
		}),
		updatePushNotificationAttribute: flow(function* (key, value) {
			let notification = self.push_notification ? JSON.parse(self.push_notification) : {}

			notification[key] = value

			self.push_notification = JSON.stringify(notification)

			console.log('self.push_notification new', self.push_notification)
		}),
		removePushNotificationImage: flow(function* () {
			let notification = self.push_notification ? JSON.parse(self.push_notification) : {}

			notification.image = null

			self.push_notification = JSON.stringify(notification)

			console.log('self.push_notification new', self.push_notification)
		}),
		openMenuEditMode: flow(function* () {
			self.in_menu_edit_mode = true
			self.current_screen.exitEditMode()
		}),
		exitMenuEditMode: flow(function* () {
			self.in_menu_edit_mode = false
		}),
		setPipeline: flow(function* (pipeline) {
			self.pipeline = PipelineStatus.create({
				ios: JSON.stringify({ 
					queue: pipeline.ios.queue,
					building: pipeline.ios.building,
					testing: pipeline.ios.testing,
					testflight: pipeline.ios.testflight,
					appstore: pipeline.ios.appstore
				}),
				android: JSON.stringify({ 
					queue: pipeline.android.queue,
					building: pipeline.android.building,
					testing: pipeline.android.testing,
					testflight: pipeline.android.testflight,
					appstore: pipeline.android.appstore
				}),
			})
		}),
		updateCurrentScreenAttribute: flow(function* (key, value) {
			// Update general screen collection
			self.screens.forEach((screen, index) => {
				if (screen.original_id === self.current_screen.original_id) {

					let snapshot = getSnapshot(self.current_screen)
					let newScreen = Screen.create(snapshot)

					if (key === 'user_level') {

						let user_levels = []
						let current_user_levels = []

						// Add existing 
						value.forEach((value) => {
							self.user_levels.forEach((level) => {
								if (level.id == value) {
									user_levels.push(UserLevel.create(getSnapshot(level)))
									current_user_levels.push(UserLevel.create(getSnapshot(level)))
								}
							})
						})

						newScreen.updateAttribute('user_levels', user_levels)

						// Update current screen instance
						self.current_screen.updateAttribute('user_levels', current_user_levels)
						self.screens[index] = newScreen

					} else { // normal key/value
						
						newScreen.updateAttribute(key, value)

						// Update current screen instance
						self.current_screen.updateAttribute(key, value)
						self.screens[index] = newScreen
					}
				}
			})
		}),
		setMediaLibrary: flow(function* (items) {
			self.media = items
		}),
		setAssetStoreLibrary: flow(function* (items) {
			self.asset_store = items
		}),
		loadScreen(screen) {

			let components = []

			screen.components.forEach((component) => {
				let individualAttributes = {
					id: `clone-attribute-${component.attributes.id}-${new Date().getTime()}`
				}

				Object.keys(component.getAttributes()).map((key) => {
					if (key === 'styles') {
						individualAttributes[key] = JSON.stringify(component.getAttributes()[key])
					} else {
						individualAttributes[key] = component.getAttributes()[key]
					}
				})

				let attributes = ComponentAttributes.create(individualAttributes)

				components.push(
					Component.create({
						id: `clone-${component.id}-${new Date().getTime()}`,
						original_id: component.original_id,
						parent_id: component.parent_id,
						type: component.type,
						attributes,
						children: component.children
					})
				)
			})

			let user_levels = []

			screen.user_levels.forEach((level) => {
				user_levels.push(UserLevel.create(getSnapshot(level)))
			})

			return Screen.create({
				id: `clone-${new Date().getTime()}`,
				original_id: screen.id,
				type: screen.type,
				is_menu: screen.is_menu,
				user_levels: user_levels,
				components
			})
		},
		setCurrentScreen: flow(function* (identifier) {
			let screenIndex = null

			if (self.current_screen) {
				self.previous_screen = self.current_screen.original_id
			}

			if (identifier === 'HEADER') {

				self.current_screen = self.loadScreen(self.header)

			} else if (identifier === 'FOOTER') {

				self.current_screen = self.loadScreen(self.footer)

			} else if (identifier === 'LOGIN_WALL') {

				self.current_screen = self.loadScreen(self.login_wall)

			} else if (identifier === 'PREMIUM_WALL') {

				self.current_screen = self.loadScreen(self.premium_wall)

			} else {

				self.screens.forEach((screen, index) => {
					if (screen.id === identifier) {
						screenIndex = index
					}
				})

				if (typeof screenIndex !== 'undefined') {
					self.current_screen = self.loadScreen(self.screens[screenIndex])
				}
			}
		}),
		addScreen: flow(function* (screen) {
			let existingScreens = []

			self.screens.forEach((screen) => {
				existingScreens.push(screen)
			})

			// Add new screen
			existingScreens.push(Screen.create({
				id: screen.id,
				original_id: screen.id,
				type: screen.type,
				is_menu: screen.is_menu,
				should_bounce: screen.should_bounce,
				user_levels: [],
				components: []
			}))

			self.screens = existingScreens
		}),
		addComponentToParentComponent: flow(function* (parentID, componentType) {

			let individualAttributes = {
				id: `unique-attribute-${new Date().getTime()}`
			}

			Object.keys(defaults[componentType].attributes).map((key) => {
				individualAttributes[key] = defaults[componentType].attributes[key]
			})

			let attributes = ComponentAttributes.create(individualAttributes)

			let defaultComponent = Component.create({
				id: `new-component-${new Date().getTime()}`,
				original_id: `new-component-${new Date().getTime()}`,
				parent_id: parentID,
				type: defaults[componentType].type,
				attributes,
				// children ?
			})

			defaultComponent.setID(`unique-${new Date().getTime()}`)

			if (defaultComponent) {

				self.current_screen.components.forEach((component, index) => {

					if (component.original_id === parentID) {

						let children = JSON.parse(component.children)
						
						children.push(defaultComponent)

						self.current_screen.components[index].children = JSON.stringify(children)

						self.current_screen.save()

						// Set this new component as the "currently being edited" component for UX.
						self.current_screen.openEditMode(defaultComponent)
					}
				})

				// self.setCurrentScreen(self.current_screen.original_id)
			}
		}),
		addComponentToCurrentScreen: flow(function* (componentType) {

			let individualAttributes = {
				id: `unique-attribute-${new Date().getTime()}`
			}

			Object.keys(defaults[componentType].attributes).map((key) => {
				individualAttributes[key] = defaults[componentType].attributes[key]
			})

			let attributes = ComponentAttributes.create(individualAttributes)

			let parentID = `new-component-${new Date().getTime()}`
			let children = []

			if (typeof defaults[componentType].attributes.children !== 'undefined' && defaults[componentType].attributes.children) {
				defaults[componentType].attributes.children.forEach((child) => {
					let childID = `new-child-${new Date().getTime()}`

					child.id = childID
					child.original_id = childID
					child.parent_id = parentID
					child.attributes.id = `new-child-attributes-${new Date().getTime()}`
					child.attributes = ComponentAttributes.create(child.attributes)
					
					children.push(Component.create(child))
				})
			}

			let defaultComponent = Component.create({
				id: parentID,
				original_id: parentID,
				type: defaults[componentType].type,
				attributes,
				children: JSON.stringify(children)
			})

			defaultComponent.setID(`unique-${new Date().getTime()}`)

			if (defaultComponent) {

				// Find current screen (source)
				let origin = null

				if (self.current_screen.original_id.toLowerCase() === 'header') {

					origin = self.header

				} else if (self.current_screen.original_id.toLowerCase() === 'footer') {

					origin = self.footer

				} else if (self.current_screen.original_id.toLowerCase() === 'login_wall') {

					origin = self.login_wall

				} else if (self.current_screen.original_id.toLowerCase() === 'premium_wall') {

					origin = self.premium_wall

				} else {

					self.screens.forEach((screen) => {
						if (screen.original_id === self.current_screen.original_id) {
							origin = screen
						}
					})
				}

				origin.addComponent(defaultComponent)

				self.setCurrentScreen(self.current_screen.original_id)
			}
		}),
		replaceCurrentScreenComponents: flow(function* (components, shouldNotSet) {

			// Find current screen (source)
			let origin = null

			if (self.current_screen.original_id.toLowerCase() === 'header') {

				origin = self.header

			} else if (self.current_screen.original_id.toLowerCase() === 'footer') {

				origin = self.footer

			} else if (self.current_screen.original_id.toLowerCase() === 'login_wall') {

				origin = self.login_wall

			} else if (self.current_screen.original_id.toLowerCase() === 'premium_wall') {

				origin = self.premium_wall

			} else {

				self.screens.forEach((screen) => {
					if (screen.original_id === self.current_screen.original_id) {
						origin = screen
					}
				})
			}

			if (origin) {
				origin.replaceComponents(components)
			}

			if (!shouldNotSet) {
				self.setCurrentScreen(self.current_screen.original_id)
			}
		}),
		setPreviewMode: flow(function* (bool) {
			self.in_preview_mode = bool
		}),
		setActiveAssetStoreItemInDetailMode: flow(function* (item) {
			self.asset_store_item_in_detail_mode = item ? MediaItem.create(getSnapshot(item)) : null
		}),
		setActiveMediaInDetailMode: flow(function* (item) {
			self.media_in_detail_mode = item ? MediaItem.create(getSnapshot(item)) : null
		}),
		retrieveMediaLibrary: flow(function* () {
			return new Promise((resolve) => {
				// Check token is valid
				USER.getValidToken().then((access_token) => {
					console.log('valid_token', access_token)
					
					fetch(`${API.base}/media/list`, {
						method: 'POST',
						headers: {
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							token: access_token
						})
					})
					.then(response => response.json())
					.then(data => {
						let items = []

						if (data.library) {
							data.library.forEach((item) => {
								items.push(MediaItem.create({
									id: `${item.id}`,
									label: `${item.label}`,
									date: `${item.date}`,
									location: `${item.location}`,
									type: `${item.type}`,
									size: `${item.size}`,
									encoding: `${item.encoding}`
								}))
							})
						}

						self.setMediaLibrary(items)
						resolve(items)
					})
					.catch((error) => {
						console.log('retrieveMediaLibrary error', error)
						resolve([])
					})
				})
			})
		}),
		retrieveAssetStoreLibrary: flow(function* () {
			return new Promise((resolve) => {
				// Check token is valid
				USER.getValidToken().then((access_token) => {
					fetch(`${API.base}/assets/list?token=${access_token}`, {
						method: 'POST'
					})
					.then(response => response.json())
					.then(data => {
						let items = []

						if (data.library) {
							data.library.forEach((item) => {
								items.push(MediaItem.create({
									id: `${item.id}`,
									label: `${item.label}`,
									date: `${item.date}`,
									location: `${item.location}`,
									type: `${item.type}`,
									size: `${item.size}`,
									encoding: `${item.encoding}`
								}))
							})
						}

						self.setAssetStoreLibrary(items)
						resolve(items)
					})
					.catch((error) => {
						console.log('retrieveMediaLibrary error', error)
						resolve([])
					})
				})
			})
		}),
		deleteMediaItem: flow(function* (mediaID) {
			return new Promise((resolve) => {
				// Check token is valid
				USER.getValidToken().then((access_token) => {
					fetch(`${API.base}/media/delete`, {
						method: 'POST',
						headers: {
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							token: access_token,
							media_id: mediaID
						})
					})
					.then(response => response.json())
					.then(data => {
						// TODO: set loading state? might be slow?
						AppModel.retrieveMediaLibrary()

						AppModel.setActiveMediaInDetailMode(null)
					})
				})
			})
		}),
		updateSettings: flow(function* (key, value) {
			let settings = self.settings ? JSON.parse(self.settings) : {}

			settings[key] = value

			self.settings = JSON.stringify(settings)

			console.log('new settings?', self.settings)
		}),
		retrieveUsers: flow(function* () {
			return new Promise((resolve) => {
				// Check token is valid
				USER.getValidToken().then((access_token) => {
					fetch(`${API.base}/users/list`, {
						method: 'POST',
						headers: {
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							token: access_token
						})
					})
					.then(response => response.json())
					.then(data => {
						console.log('getUsers response', data)

						let items = []

						if (data.users) {

							data.users.forEach((user) => {
								items.push(User.create({
									uid: user.uid,
									email: user.email,
									email_verified: user.email_verified,
									created: user.created
								}))
							})
						}

						self.setUsers(items)

						resolve(items)
					})
					.catch((error) => {
						console.log('getUsers error', error)
						resolve([])
					})
				})
			})
		}),
		setUsers: flow(function* (users) {
			self.users = users
		})
    }))
    .views(self => ({
		get_settings() {
			console.log('get_settings', self.settings)
			return self.settings ? JSON.parse(self.settings) : {}
		},
		get_font_families() {

			let allFonts = [
				{
					name: 'Roboto',
					levels: [ 1, 2, 3, 4 ]
				},
				{
					name: 'Open Sans',
					levels: [ 1, 2, 3, 4 ]
				},
				{
					name: 'Lato',
					levels: [ 1, 2, 3, 4 ]
				},
				{
					name: 'Be Vietnam Pro',
					levels: [ 2, 3, 4 ]
				}
			]

			let fonts = []

			allFonts.forEach((font) => {
				if (font.levels.includes(USER.account_type)) {
					fonts.push({ key: font.name, value: font.name, text: font.name })
				}
			})
			
			return fonts
		},
		get_computed_pipeline() {

			let pipeline = null

			if (self.pipeline && self.pipeline.ios && JSON.parse(self.pipeline.ios)) {
				pipeline = {
					ios: { 
						queue: JSON.parse(self.pipeline.ios).queue,
						building: JSON.parse(self.pipeline.ios).building,
						testing: JSON.parse(self.pipeline.ios).testing,
						testflight: JSON.parse(self.pipeline.ios).testflight,
						appstore: JSON.parse(self.pipeline.ios).appstore,
					},
					android: { 
						queue: JSON.parse(self.pipeline.android).queue,
						building: JSON.parse(self.pipeline.android).building,
						testing: JSON.parse(self.pipeline.android).testing,
						testflight: JSON.parse(self.pipeline.android).testflight,
						appstore: JSON.parse(self.pipeline.android).appstore,
					},
				}
			}

			return pipeline
		},
		inPreviewMode() {
			return self.in_preview_mode ? true : false
		},
		getCurrentScreen() {
			return self.current_screen
		},
		renderCurrentScreen() {
			return <Renderer screen={ self.current_screen } header={ self.header } footer={ self.footer } login_wall={ self.login_wall } premium_wall={ self.premium_wall } menu={ self.menu } />
		},
		getScreenList() {
			
			let list = []

			// Mandatory screens
			list.push({ key: `HEADER`, value: `HEADER`, text: `Header` })
			list.push({ key: `FOOTER`, value: `FOOTER`, text: `Footer` })
			list.push({ key: `login_wall`, value: `LOGIN_WALL`, text: `Login Wall` })
			list.push({ key: `premium_wall`, value: `PREMIUM_WALL`, text: `Premium Wall` })
			list.push({ key: `menu`, value: `MENU`, text: `Menu` })
			list.push({ key: `home`, value: `HOME`, text: `Home` })

			self.screens.forEach((screen) => {
				if (screen.original_id !== 'HOME' && screen.original_id !== 'MENU') {
					list.push({ key: screen.id, value: screen.id, text: screen.original_id })
				}
			})

			return list
		},
		renderOutput() {
			return JSON.stringify(getSnapshot(AppModel))
		},
		getAssetStoreLibrary() {
			return self.asset_store
		},
		getMediaLibrary() {
            return self.media
		},
		getMediaItemByID(id) {
			let item = null

			self.media.forEach((media) => {
				if (media.id === id) {
					item = media.location
				}
			})

			return item ? item : null
		},
		getMediaItemLocationByID(id) {
			let item = null

			let mediaLibrary = self.getMediaLibrary()

			mediaLibrary.map((media) => {
				if (media.id === id) {
					item = media
				}
			})

			return item && item.location ? item.location : `data:image/svg+xml;base64,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`
		},
		getMediaLibraryList() {
			let list = []

			let mediaLibrary = self.getMediaLibrary()

			mediaLibrary.map((media) => {
				if (media.type === 'image/jpeg' || media.type === 'image/png' || media.type === 'image/gif' || media.type === 'image/bmp' || media.type === 'image/svg+xml') {
					list.push(<a href="#" onClick={() => self.setActiveMediaInDetailMode(media)}>
						<img src={`${media.location}`} />
						<span>{ media.label }</span>
					</a>)
				} else if (media.type === 'video/mp4') {
					list.push(<a href="#" onClick={() => self.setActiveMediaInDetailMode(media)}>
						<video src={`${media.location}`} autoPlay={false} muted loop={true} playsinline controls width="100%" height="100%" />
						<span>{ media.label }</span>
					</a>)
				}
			})

			return list
		},
		getActiveMediaItemDetails() {
			if (self.media_in_detail_mode) {
				return {
					id: self.media_in_detail_mode.id,
					label: self.media_in_detail_mode.label,
					date: self.media_in_detail_mode.date,
					location: self.media_in_detail_mode.location,
					type: self.media_in_detail_mode.type
				}
			}

			return null
		},
		getAssetStoreList() {
			let list = []

			let mediaLibrary = self.getAssetStoreLibrary()

			mediaLibrary.map((media) => {
				if (media.type === 'image/jpeg' || media.type === 'image/png' || media.type === 'image/gif' || media.type === 'image/bmp' || media.type === 'image/svg+xml') {
					list.push(<a href="#" onClick={() => self.setActiveAssetStoreItemInDetailMode(media)}>
						<img src={`${media.location}`} />
						<span>{ media.label }</span>
					</a>)
				} else if (media.type === 'video/mp4') {
					list.push(<a href="#" onClick={() => self.setActiveAssetStoreItemInDetailMode(media)}>
						<video src={`${media.location}`} autoPlay={false} muted loop={true} playsinline controls width="100%" height="100%" />
						<span>{ media.label }</span>
					</a>)
				}
			})

			return list
		},
		getActiveAssetStoreItemDetails() {
			if (self.asset_store_item_in_detail_mode) {
				return {
					id: self.asset_store_item_in_detail_mode.id,
					label: self.asset_store_item_in_detail_mode.label,
					date: self.asset_store_item_in_detail_mode.date,
					location: self.asset_store_item_in_detail_mode.location,
					type: self.asset_store_item_in_detail_mode.type
				}
			}

			return null
		},
		getUsers() {
			return self.users
		},
		getUserCount() {
			return self.users.length
		},
		getUserList() {
			let list = []

			let users = self.getUsers()

			if (users) {

				console.log('getUserList', users)

				users.map((user) => {
					console.log('getUserList -> user', user, user.email, user.created)
					
					list.push(<a href="#" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<strong>{ user.email }</strong>
						<em style={{ fontSize: 11 }}>Signed up: { user.created }</em>
					</a>)
				})
			}

			return list
		},
	}))
	.create();

onSnapshot(AppModel, snapshot => typeof window !== 'undefined' ? window.localStorage.setItem('AppModel', JSON.stringify(snapshot)) : null);

export default AppModel;

